import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { ApiError, apiGet, apiSend } from "../../lib/customFetch";
import styles from "./KioskPage.module.css";

interface KioskEmployee {
  id: string;
  navn: string;
}

interface StampResult {
  action: "clocked_in" | "clocked_out";
  at: string;
}

type KioskState =
  | { status: "loading" }
  | { status: "ready"; employees: KioskEmployee[] }
  | { status: "unpaired" }
  | { status: "unavailable" }
  | { status: "failed" };

type Feedback =
  | { kind: "success"; text: string }
  | { kind: "error"; text: string }
  | { kind: "info"; text: string }
  | null;

const RESET_MS = 30_000;
const pairingClaims = new Map<string, Promise<{ redirectTo: string }>>();

/** Del samme request mellem StrictModes to dev-mounts; fjern den igen efter svar. */
function claimPairing(token: string): Promise<{ redirectTo: string }> {
  const existing = pairingClaims.get(token);
  if (existing !== undefined) return existing;
  const request = apiSend<{ redirectTo: string }>("POST", `/api/kiosk/pair/${token}`);
  pairingClaims.set(token, request);
  void request.finally(() => pairingClaims.delete(token)).catch(() => undefined);
  return request;
}

function kioskFejl(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 429) return "For mange forsøg. Vent fem minutter.";
    if (error.status === 409) return error.message;
    if (error.status === 401) return "Koden kunne ikke godkendes. Prøv igen.";
    if (error.status >= 500) {
      return "Kiosken kunne ikke kontaktes. Vælg samme medarbejder og prøv igen.";
    }
  }
  return "Forbindelsen til kiosken blev afbrudt. Vælg samme medarbejder og prøv igen.";
}

export function KioskPairingPage({ token }: { token: string }) {
  const [, setLocation] = useLocation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    claimPairing(token)
      .then((result) => {
        if (active) setLocation(result.redirectTo, { replace: true });
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setError(
          reason instanceof ApiError
            ? reason.message
            : "Skærmen kunne ikke forbindes.",
        );
      });
    return () => {
      active = false;
    };
  }, [setLocation, token]);

  return (
    <main className={styles.centered}>
      <section className={styles.messageCard} aria-live="polite">
        <p className={styles.brand}>AppSmeden kiosk</p>
        {error === null ? (
          <>
            <span className={styles.spinner} aria-hidden="true" />
            <h1>Forbinder kiosken…</h1>
            <p>Skærmen bliver klar om et øjeblik.</p>
          </>
        ) : (
          <>
            <h1>Skærmen kunne ikke forbindes</h1>
            <p role="alert">{error}</p>
            <p>Bed en administrator om at oprette et nyt engangslink.</p>
          </>
        )}
      </section>
    </main>
  );
}

export function KioskPage() {
  const [state, setState] = useState<KioskState>({ status: "loading" });
  const [selected, setSelected] = useState<KioskEmployee | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const retryIntent = useRef<{ employeeId: string; clientId: string } | null>(null);

  const loadEmployees = useCallback(() => {
    setState({ status: "loading" });
    apiGet<KioskEmployee[]>("/api/kiosk/employees")
      .then((employees) => setState({ status: "ready", employees }))
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 401) {
          setState({ status: "unpaired" });
        } else if (error instanceof ApiError && error.status === 404) {
          setState({ status: "unavailable" });
        } else {
          setState({ status: "failed" });
        }
      });
  }, []);

  useEffect(() => {
    loadEmployees();
    window.addEventListener("online", loadEmployees);
    return () => window.removeEventListener("online", loadEmployees);
  }, [loadEmployees]);

  useEffect(() => {
    if (busy || (selected === null && code.length === 0)) return;
    const timeout = window.setTimeout(() => {
      retryIntent.current = null;
      setSelected(null);
      setCode("");
      setFeedback({ kind: "info", text: "Valget blev nulstillet efter 30 sekunder." });
    }, RESET_MS);
    return () => window.clearTimeout(timeout);
  }, [selected, code, busy]);

  useEffect(() => {
    if (feedback === null) return;
    const timeout = window.setTimeout(() => setFeedback(null), 3_000);
    return () => window.clearTimeout(timeout);
  }, [feedback]);

  const reset = (clearIntent = false) => {
    if (clearIntent) retryIntent.current = null;
    setSelected(null);
    setCode("");
  };

  const choose = (employee: KioskEmployee) => {
    if (retryIntent.current?.employeeId !== employee.id) retryIntent.current = null;
    setSelected(employee);
    setCode("");
    setFeedback(null);
  };

  const digit = (value: string) => {
    if (!busy) setCode((current) => `${current}${value}`.slice(0, 4));
  };

  const stamp = () => {
    if (selected === null || code.length !== 4 || busy) return;
    setBusy(true);
    const employeeName = selected.navn;
    const clientId =
      retryIntent.current?.employeeId === selected.id
        ? retryIntent.current.clientId
        : crypto.randomUUID();
    retryIntent.current = { employeeId: selected.id, clientId };
    apiSend<StampResult>("POST", "/api/kiosk/stamp", {
      employeeId: selected.id,
      code,
      clientId,
    })
      .then((result) => {
        retryIntent.current = null;
        setBusy(false);
        reset();
        const action = result.action === "clocked_in" ? "stemplet ind" : "stemplet ud";
        const time = new Date(result.at).toLocaleTimeString("da-DK", {
          hour: "2-digit",
          minute: "2-digit",
        });
        setFeedback({ kind: "success", text: `${employeeName} er ${action} kl. ${time}.` });
      })
      .catch((error: unknown) => {
        setBusy(false);
        if (error instanceof ApiError && error.status === 404) {
          retryIntent.current = null;
          reset();
          setState({ status: "unavailable" });
          return;
        }
        const transient =
          !(error instanceof ApiError) || error.status === 429 || error.status >= 500;
        if (!transient) retryIntent.current = null;
        reset();
        if (error instanceof ApiError && error.status === 401 && error.code === undefined) {
          // Både forkert kode og en tilbagekaldt session er 401. Bekræft
          // sessionen med den ufarlige list-route, før pairingtilstand vises.
          apiGet<KioskEmployee[]>("/api/kiosk/employees")
            .then(() => setFeedback({ kind: "error", text: kioskFejl(error) }))
            .catch((check: unknown) => {
              if (check instanceof ApiError && check.status === 401) {
                setState({ status: "unpaired" });
              } else {
                setFeedback({ kind: "error", text: kioskFejl(error) });
              }
            });
          return;
        }
        setFeedback({ kind: "error", text: kioskFejl(error) });
      });
  };

  if (state.status !== "ready") {
    const content =
      state.status === "loading"
        ? { title: "Henter medarbejdere…", text: "Kiosken bliver klar om et øjeblik." }
        : state.status === "unpaired"
          ? { title: "Kiosken skal forbindes", text: "Bed en administrator om at åbne et nyt engangslink på denne skærm." }
          : state.status === "unavailable"
            ? { title: "Kiosken er ikke aktiv", text: "Tidsregistrering er slået fra for virksomheden." }
            : { title: "Kunne ikke hente kiosken", text: "Tjek forbindelsen og prøv igen." };
    return (
      <main className={styles.centered}>
        <section className={styles.messageCard} aria-live="polite">
          <p className={styles.brand}>AppSmeden kiosk</p>
          <h1>{content.title}</h1>
          <p>{content.text}</p>
          {state.status === "failed" && (
            <button className={styles.retry} type="button" onClick={loadEmployees}>
              Prøv igen
            </button>
          )}
        </section>
      </main>
    );
  }

  return (
    <main className={styles.kiosk}>
      <header className={styles.header}>
        <div>
          <p className={styles.brand}>AppSmeden kiosk</p>
          <h1>{selected === null ? "Hvem er du?" : `Hej ${selected.navn}`}</h1>
        </div>
        {selected !== null && (
          <button className={styles.cancel} type="button" onClick={() => reset(true)} disabled={busy}>
            Start forfra
          </button>
        )}
      </header>

      {feedback !== null && (
        <div
          className={
            feedback.kind === "success"
              ? styles.success
              : feedback.kind === "error"
                ? styles.error
                : styles.info
          }
          role={feedback.kind === "error" ? "alert" : "status"}
        >
          {feedback.text}
        </div>
      )}

      {selected === null ? (
        state.employees.length === 0 ? (
          <section className={styles.empty}>
            <h2>Ingen medarbejdere er klar</h2>
            <p>En administrator skal opsætte en kioskkode på mindst én medarbejder.</p>
          </section>
        ) : (
          <section className={styles.employeeGrid} aria-label="Vælg medarbejder">
            {state.employees.map((employee) => (
              <button
                className={styles.employee}
                type="button"
                key={employee.id}
                onClick={() => choose(employee)}
              >
                {employee.navn}
              </button>
            ))}
          </section>
        )
      ) : (
        <section className={styles.codePanel}>
          <p className={styles.instruction}>Indtast din firecifrede kode</p>
          <div className={styles.codeDots} aria-label={`${code.length} af 4 cifre indtastet`}>
            {[0, 1, 2, 3].map((index) => (
              <span key={index} className={index < code.length ? styles.dotFilled : styles.dot} />
            ))}
          </div>
          <div className={styles.keypad} aria-label="Taltastatur">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((value) => (
              <button key={value} type="button" onClick={() => digit(value)} disabled={busy}>
                {value}
              </button>
            ))}
            <button type="button" onClick={() => setCode("")} disabled={busy || code.length === 0}>
              Ryd
            </button>
            <button type="button" onClick={() => digit("0")} disabled={busy}>0</button>
            <button type="button" onClick={() => setCode((current) => current.slice(0, -1))} disabled={busy || code.length === 0}>
              Slet
            </button>
          </div>
          <button className={styles.stampButton} type="button" onClick={stamp} disabled={busy || code.length !== 4}>
            {busy ? "Stempler…" : "Stempel nu"}
          </button>
        </section>
      )}
    </main>
  );
}
